// Price/candlestick chart for the trading page.
//
// Two implementations sit behind one component, chosen by a feature flag:
//
//   NATIVE_CANDLE_CHART on (DEFAULT) -> NativeCandlesChart, drawn from OUR OWN
//                              fills (GET /candles + the `candles` websocket
//                              channel, both fed by the indexer's OHLCV buckets).
//   NATIVE_CANDLE_CHART off          -> TvAdvancedChart, the TradingView tv.js
//                              embed. Opt in with
//                              REACT_APP_FEATURE_NATIVE_CANDLE_CHART=false.
//
// The distinction matters more than "which library draws it". The tv.js widget
// charts a THIRD-PARTY venue for the symbol — `getTradingViewSymbol` maps
// BTC-PERP to OKX:BTCUSD.P, XAU-PERP to OANDA:XAUUSD, TSLA-PERP to NASDAQ:TSLA
// — so with the flag off the chart shows prices from somewhere else entirely,
// and it will not move when this exchange trades. That is why native is the
// default: on a trading page, the chart should be this book. A market with no
// fills yet says "No trades yet" rather than borrowing another venue's candles.
//
// The flag is read per render rather than captured at module scope, so the
// `?ff=NATIVE_CANDLE_CHART:true` and localStorage QA overrides work without a
// rebuild. The component keeps the same name, props and default export it has
// always had, so every caller (PerpsOrderbookAndChart, MobilePerpsSection, ...)
// mounts it unchanged.
import { FeatureFlagKey, isFeatureEnabled } from "../../config/features";
import type { ITradingViewChartProps } from "./LightweightCandles";
import NativeCandlesChart from "./NativeCandlesChart";
import TvAdvancedChart from "./TvAdvancedChart";
import TvLibraryChart from "./TvLibraryChart";

// With the native chart on, ADVANCED_CHART_UI (default on) renders those same
// candles — our own fills, never another venue's — inside TradingView Advanced
// Charts, the self-hosted Charting Library. TvLibraryChart falls back to
// NativeCandlesChart by itself when the library bundle is not deployed.
function TradingViewChart(props: ITradingViewChartProps) {
  if (isFeatureEnabled(FeatureFlagKey.NATIVE_CANDLE_CHART)) {
    return isFeatureEnabled(FeatureFlagKey.ADVANCED_CHART_UI) ? (
      <TvLibraryChart {...props} />
    ) : (
      <NativeCandlesChart {...props} />
    );
  }
  return <TvAdvancedChart {...props} />;
}

export type { ITradingViewChartProps };
export default TradingViewChart;
